import React, { useContext, useEffect, useState } from "react";

import { t } from "i18next";
import { useFormContext } from "react-hook-form";

import {
  InputGroup,
  InputGroupAddon,
  InputGroupInputBase,
} from "@/src/components/form-fields/InputGroup";
import {
  CURRENCY_OPTION_LIST,
  currencyMin,
} from "@/src/components/offers/offersFormParams/offersFormParams";
import { Field, FieldError, FieldLabel } from "@/src/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/src/components/ui/select";

import PlanDrawerContainerContext, {
  PLAN_DRAWER_POPOVER_CLASS,
} from "../plan-drawer-context";

import type { ChangeEvent, JSX } from "react";

import type { PlanFormValues } from "@/src/components/offers/offerPlanAdapter";

interface PlanPriceFieldProps {
  disabled?: boolean;
}

function PlanPriceField({ disabled }: PlanPriceFieldProps): JSX.Element {
  const portalContainer = useContext(PlanDrawerContainerContext);
  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<PlanFormValues>();
  const currency = watch("currency") ?? "EUR";
  const price = watch("price") ?? 0;
  const error = errors.price?.message;
  const min = currencyMin[currency as keyof typeof currencyMin] ?? 1;
  // Local text state so the field can be cleared / edited freely — the bound
  // number would otherwise be coerced to the currency minimum on every change,
  // making it impossible to empty. Resync only on external changes (currency
  // clamp, plan reset), never while the user is typing.
  const [rawPrice, setRawPrice] = useState<string>(String(price));

  useEffect(() => {
    if (Number(rawPrice) !== price) setRawPrice(String(price));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [price]);

  const handlePriceChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const raw = event.target.value;
    setRawPrice(raw);
    setValue("price", raw === "" ? 0 : Number(raw), {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <Field>
      <FieldLabel>{t("editor-offers:price")}</FieldLabel>
      <InputGroup invalid={Boolean(error)}>
        <InputGroupAddon>
          <Select
            disabled={disabled}
            value={currency}
            onValueChange={(val: string) =>
              setValue("currency", val, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent
              align="start"
              className={PLAN_DRAWER_POPOVER_CLASS}
              container={portalContainer}
            >
              {CURRENCY_OPTION_LIST.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.symbol} — {option.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </InputGroupAddon>
        <InputGroupInputBase
          className="h-10 text-lg"
          disabled={disabled}
          min={min}
          onChange={handlePriceChange}
          step={1}
          type="number"
          value={rawPrice}
        />
      </InputGroup>
      <FieldError>{error}</FieldError>
    </Field>
  );
}

export default PlanPriceField;
